import { useEffect, useMemo, useRef, useState } from 'react'
import { fetchProjects, normalizePartProjects } from '../services/projectApi'

export function projectIdOf(item) {
  return String(item?.projectId || item?.id || '').trim()
}

export function projectNameOf(item) {
  return item?.projectName || item?.name || ''
}

export function ProjectBadges({ projects }) {
  const items = normalizePartProjects(projects)
  if (!items.length) return <span className="muted">연결된 프로젝트 없음</span>
  return (
    <div className="project-badge-list">
      {items.map((item) => (
        <span key={item.projectId} className="chip chip--static">
          {item.projectName || item.projectId}
        </span>
      ))}
    </div>
  )
}

export default function ProjectPicker({
  value,
  onChange,
  multiple = true,
  activeOnly = true,
  placeholder = '프로젝트 검색',
  emptyLabel = '전체',
  disabled = false,
}) {
  const selected = useMemo(() => (multiple ? normalizePartProjects(value) : []), [multiple, value])
  const selectedId = multiple ? '' : String(value || '').trim()
  const [projects, setProjects] = useState([])
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const rootRef = useRef(null)

  useEffect(() => {
    let cancelled = false
    fetchProjects(activeOnly ? { active: true } : {})
      .then((items) => {
        if (!cancelled) setProjects(items)
      })
      .catch(() => {
        if (!cancelled) setProjects([])
      })
    return () => {
      cancelled = true
    }
  }, [activeOnly])

  useEffect(() => {
    function handleClick(event) {
      if (!rootRef.current?.contains(event.target)) setOpen(false)
    }
    function handleKey(event) {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [])

  const catalog = useMemo(() => {
    const byId = new Map(projects.map((item) => [item.id, item]))
    if (multiple) {
      selected.forEach((item) => {
        if (!byId.has(item.projectId)) {
          byId.set(item.projectId, { id: item.projectId, name: item.projectName, active: false })
        }
      })
    } else if (selectedId && !byId.has(selectedId)) {
      byId.set(selectedId, { id: selectedId, name: selectedId, active: false })
    }
    return [...byId.values()].sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'ko'))
  }, [projects, multiple, selected, selectedId])

  const selectedIds = useMemo(
    () => new Set(multiple ? selected.map((item) => item.projectId) : selectedId ? [selectedId] : []),
    [multiple, selected, selectedId],
  )

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return catalog.filter((item) => {
      if (multiple && selectedIds.has(item.id)) return false
      if (!q) return true
      return (
        String(item.name || '').toLowerCase().includes(q) ||
        String(item.sourceFolderName || '').toLowerCase().includes(q)
      )
    })
  }, [catalog, query, multiple, selectedIds])

  function emitMultiple(next) {
    onChange(normalizePartProjects(next))
  }

  function addProject(item) {
    if (disabled) return
    if (multiple) {
      emitMultiple([...selected, { projectId: item.id, projectName: item.name }])
      setQuery('')
      return
    }
    onChange(item.id)
    setQuery('')
    setOpen(false)
  }

  function removeProject(projectId) {
    if (disabled) return
    if (multiple) {
      emitMultiple(selected.filter((item) => item.projectId !== projectId))
      return
    }
    onChange('')
  }

  const selectedSingle = !multiple ? catalog.find((item) => item.id === selectedId) : null

  return (
    <div className={`project-picker${disabled ? ' is-disabled' : ''}`} ref={rootRef}>
      {multiple ? (
        <div className="project-picker__chips">
          {selected.map((item) => (
            <span key={item.projectId} className="project-picker__chip">
              {item.projectName || item.projectId}
              <button
                type="button"
                className="project-picker__remove"
                aria-label={`${item.projectName || item.projectId} 제거`}
                onClick={() => removeProject(item.projectId)}
                disabled={disabled}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      ) : selectedSingle ? (
        <div className="project-picker__chips">
          <span className="project-picker__chip">
            {selectedSingle.name}
            {selectedSingle.active === false ? ' (비활성)' : ''}
            <button
              type="button"
              className="project-picker__remove"
              aria-label="선택 해제"
              onClick={() => removeProject(selectedSingle.id)}
              disabled={disabled}
            >
              ×
            </button>
          </span>
        </div>
      ) : null}

      <div className="project-picker__control">
        <input
          className="input"
          value={query}
          disabled={disabled}
          placeholder={multiple || !selectedId ? placeholder : emptyLabel}
          onChange={(event) => {
            setQuery(event.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          autoComplete="off"
        />
        {!multiple && !selectedId ? <span className="project-picker__hint">{emptyLabel}</span> : null}
      </div>

      {open && !disabled ? (
        <div className="project-picker__menu" role="listbox">
          {!multiple ? (
            <button type="button" className="project-picker__option" onClick={() => removeProject('')}>
              {emptyLabel}
            </button>
          ) : null}
          {filtered.length === 0 ? (
            <p className="project-picker__empty">검색 결과가 없습니다.</p>
          ) : (
            filtered.map((item) => (
              <button
                key={item.id}
                type="button"
                className="project-picker__option"
                onClick={() => addProject(item)}
              >
                <strong>{item.name}</strong>
                {item.active === false ? <span className="muted">비활성</span> : null}
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  )
}
